import {
  getArdoqErrorTraceId,
  isArdoqError,
  toArdoqError,
  type ArdoqError,
} from '@ardoq/common-helpers';
import { confirm, modal } from '@ardoq/modal';
import type {
  RuleBasedEditFieldColumn,
  RuleBasedEditFieldData,
  RuleBasedEditCommands,
} from '../../types';
import { getConfirmMultiUpdateFailedDialog } from '../confirmDialogs';
import { BulkEditFieldDialog } from './BulkEditFieldDialog';
import type { I18nLocale } from '@ardoq/i18n';
import type { Locale } from '@ardoq/locale';

const showFailure = (i18nLocale: I18nLocale, error: ArdoqError) =>
  confirm(
    getConfirmMultiUpdateFailedDialog(
      i18nLocale,
      error,
      getArdoqErrorTraceId(error)
    )
  );

const executeBulkUpdate = async (
  update: RuleBasedEditFieldData,
  i18nLocale: I18nLocale,
  commands: RuleBasedEditCommands,
  refreshServerSideForGridCallback: () => void
): Promise<void> => {
  const result = await commands.executeRuleBasedEdit(update);
  if (isArdoqError(result)) {
    showFailure(i18nLocale, result);
    return;
  }
  refreshServerSideForGridCallback();
};

export const openBulkEditFieldDialog = async ({
  preselectedFieldName,
  i18nLocale,
  locale,
  commands,
  refreshServerSideForGridCallback,
  allBulkEditableColumns,
}: {
  preselectedFieldName: string | null;
  i18nLocale: I18nLocale;
  locale: Locale;
  commands: RuleBasedEditCommands;
  refreshServerSideForGridCallback: () => void;
  allBulkEditableColumns: RuleBasedEditFieldColumn[];
}): Promise<void> => {
  try {
    if (allBulkEditableColumns.length === 0) {
      return;
    }

    const update = await modal<RuleBasedEditFieldData>(resolve => (
      <BulkEditFieldDialog
        columns={allBulkEditableColumns}
        preselectedFieldName={
          preselectedFieldName ?? allBulkEditableColumns[0].fieldName
        }
        getBulkEditAffectedComponentCount={
          commands.getRuleBaedEditComponentPreview
        }
        i18nLocale={i18nLocale}
        locale={locale}
        onCancel={() => resolve(false)}
        onSubmit={resolve}
      />
    ));

    if (!update) {
      return;
    }

    await executeBulkUpdate(
      update,
      i18nLocale,
      commands,
      refreshServerSideForGridCallback
    );
  } catch (error) {
    showFailure(i18nLocale, toArdoqError({ error: error as Error }));
  }
};
